import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router";
import { validateEmail } from "helpers/validations";
import { useState, useRef, useCallback } from "react";
import { submitDemoRequest } from "redux/Modals/actions";
import { useOnClickOutside } from "hooks/useOnClickOutside";

import FloatingLabelInput from "./FloatingLabelInput";

import styles from "./style.module.scss";
import { FormDataKeys } from "types";

export const ERROR_MESSAGES = {
  required: "This field is required",
  invalidEmail: "Please enter a valid email address",
};

type InputNameTypes = "email" | "fullName" | "company";

const FreeTrialPage = () => {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");

  const [emailError, setEmailError] = useState<string | null>(null);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [fullNameError, setFullNameError] = useState<string | null>(null);

  const [focusedInput, setFocusedInput] = useState<InputNameTypes | null>(null);
  const [showFormSubmissionError, setShowFormSubmissionError] = useState(false);

  const emailInputRef = useRef<HTMLDivElement>(null);
  const companyInputRef = useRef<HTMLDivElement>(null);
  const fullNameInputRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailError = !email
      ? ERROR_MESSAGES.required
      : validateEmail(email)
        ? ERROR_MESSAGES.invalidEmail
        : null;
    const companyError = !company ? ERROR_MESSAGES.required : null;
    const fullNameError = !fullName ? ERROR_MESSAGES.required : null;

    setEmailError(emailError);
    setCompanyError(companyError);
    setFullNameError(fullNameError);

    if (!emailError && !fullNameError && !companyError) {
      const formDataKeys: FormDataKeys = {
        email,
        company,
        fullName,
        calledFrom: "free_trial",
      };
      const success = await dispatch(submitDemoRequest(formDataKeys) as any);

      if (success) {
        navigate("/thank-you");
      }
      setShowFormSubmissionError(!success);
    }
  };

  const handleInputFocus = (inputName: InputNameTypes) => {
    setFocusedInput(inputName);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailError(null);
  };

  const handleFullNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFullName(e.target.value);
    setFullNameError(null);
  };

  const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompany(e.target.value);
    setCompanyError(null);
  };

  const handleSignInClick = () => {
    let path = "/login";
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.toString() !== "") {
      path = path + "?" + searchParams.toString();
    }
    navigate(path);
  };

  const handleClickOutside = useCallback(() => {
    setFocusedInput(null);
  }, []);

  useOnClickOutside(
    emailInputRef,
    handleClickOutside,
    focusedInput === "email",
  );
  useOnClickOutside(
    companyInputRef,
    handleClickOutside,
    focusedInput === "company",
  );
  useOnClickOutside(
    fullNameInputRef,
    handleClickOutside,
    focusedInput === "fullName",
  );

  return (
    <div className={styles.formSection}>
      <div className={styles.formHeader}>
        <div className={styles.formTitle}>Unlock your free trial</div>
        <div className={styles.formDescription}>
          Manage and analyze image and video data effortlessly.
        </div>
      </div>

      {/* Form Section */}
      <form
        id="free-trial-form"
        data-form-id="free-trial-form"
        className={styles.form}
        onSubmit={handleSubmit}
        noValidate
      >
        <div className={styles.formInputGroup}>
          <FloatingLabelInput
            id="email"
            value={email}
            inputType="email"
            inputRef={emailInputRef}
            errorMessage={emailError}
            isFocused={focusedInput === "email"}
            label={`Email${emailError ? " *" : ""}`}
            onChange={handleEmailChange}
            onFocus={() => handleInputFocus("email")}
          />

          <FloatingLabelInput
            inputType="text"
            id="fullName"
            value={fullName}
            inputRef={fullNameInputRef}
            errorMessage={fullNameError}
            isFocused={focusedInput === "fullName"}
            label={`Full Name${fullNameError ? " *" : ""}`}
            onChange={handleFullNameChange}
            onFocus={() => handleInputFocus("fullName")}
          />

          <FloatingLabelInput
            inputType="text"
            id="company"
            value={company}
            inputRef={companyInputRef}
            errorMessage={companyError}
            isFocused={focusedInput === "company"}
            label={`Company${companyError ? " *" : ""}`}
            onChange={handleCompanyChange}
            onFocus={() => handleInputFocus("company")}
          />
        </div>

        <FloatingLabelInput
          inputType="text"
          id="mobile"
          value={""}
          errorMessage={null}
          isFocused={false}
          label={`Mobile`}
          onChange={() => {}}
          onFocus={() => {}}
          hidden={true}
        />

        <FloatingLabelInput
          inputType="text"
          id="title"
          value={""}
          errorMessage={null}
          isFocused={false}
          label={`Title`}
          onChange={() => {}}
          onFocus={() => {}}
          hidden={true}
        />

        <button
          type="submit"
          className={styles.submitButton}
          data-testid="free-trial-send-button"
        >
          Send
        </button>

        <span className={styles.signInText}>
          Already have an account?
          <span className={styles.link} onClick={handleSignInClick}>
            Sign in
          </span>
        </span>
        {showFormSubmissionError && (
          <div className={styles.formSubmissionError}>
            Something went wrong. Please try again.
          </div>
        )}
      </form>
    </div>
  );
};

export default FreeTrialPage;
